import { Skeleton } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, AudioLines, Film, Image as ImageIcon } from "lucide-react";
import { useState } from "react";

import { useGetCatalog } from "../../api/generated/endpoints/generation/generation";
import type { Job, JobStatus } from "../../api/generated/model";
import { accessScopeQuery } from "../../api/session";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { GenerationDetailModal } from "../../platform/jobs/detail/GenerationDetailModal";
import { useDownloadOutput } from "../../platform/jobs/download-output";
import { expiredLine, isExpired } from "../../platform/jobs/expired";
import { useJobList } from "../../platform/jobs/job-list";
import { modelTitle } from "../../platform/jobs/model-name";
import { isSettled } from "../../platform/jobs/status";
import { RunningMedia } from "./RunningMedia";
import { TYPE_NAMES } from "../../platform/jobs/type-names";
import { useStreamStatus } from "../../platform/stream-status";
import { ThumbImage } from "../../ui/ThumbImage";
import { SectionHead } from "./SectionHead";
import classes from "./home2.module.css";
import { SoonControl } from "./Soon";

/** The brand's newest jobs read for the row: enough to find five of the user's own with media. */
const READ = 20;
const SHOWN = 5;

type Medium = "image" | "video" | "audio";

/** A job's medium from its type (`image.simple`), or `undefined` for one with nothing to show. */
function mediumOf(job: Job): Medium | undefined {
  const kind = job.type.split(".")[0];
  return kind === "image" || kind === "video" || kind === "audio" ? kind : undefined;
}

/**
 * The prototype's order: unfinished work first (running, then failed), then the latest results,
 * each group newest first as the list reads them.
 */
export function continueJobs(jobs: readonly Job[], userId: string | undefined): Job[] {
  const mine = jobs.filter((job) => job.created_by === userId && mediumOf(job) !== undefined);
  const rank = (job: Job) => (!isSettled(job.status) ? 0 : job.status === "failed" ? 1 : 2);
  return [...mine].sort((one, other) => rank(one) - rank(other)).slice(0, SHOWN);
}

/** The prototype's status line, in its words. */
const STATUS: Readonly<Record<JobStatus, { text: string; tone: "v" | "ok" | "err" }>> = {
  queued: { text: "Running", tone: "v" },
  submitting: { text: "Running", tone: "v" },
  running: { text: "Running", tone: "v" },
  finalizing: { text: "Running", tone: "v" },
  succeeded: { text: "Ready", tone: "ok" },
  failed: { text: "Failed, credits returned", tone: "err" },
  expired: { text: "Expired", tone: "err" },
  canceled: { text: "Canceled", tone: "err" },
};

/** The usual next step the prototype offers for a result: a Create page's (decision 10). None
 *  while it runs, and none for an expired or canceled job. */
function nextStep(job: Job, medium: Medium): string | null {
  if (job.status === "failed") return "Retry";
  if (job.status !== "succeeded") return null;
  if (job.type.startsWith("video.talking_head")) return "Publish as ad";
  return medium === "image" ? "Animate" : medium === "video" ? "Upscale" : "Use in Lip-sync";
}

/**
 * The output a card shows and downloads: the first with a file, as the Generations page's card
 * picks it, so a job whose first output failed shows and downloads the next (the Pro walk on dev).
 */
export function shownOutput(job: Job): Job["outputs"][number] | undefined {
  return job.outputs.find((each) => each.url !== null);
}

function Picture({ job, medium, onLost }: { job: Job; medium: Medium; onLost: () => void }) {
  const output = shownOutput(job);
  // A thumbnail or a video's still, never the file itself unless the thumbnail is not made yet.
  if (medium === "image" && output?.url != null) {
    return <ThumbImage thumb={output.thumb_url} full={output.url} alt="" onLost={onLost} />;
  }
  if (medium === "video" && output?.poster_url != null) {
    // A poster's signed URL that fails reads the list again, as a thumbnail's does.
    return <img src={output.poster_url} alt="" loading="lazy" decoding="async" onError={onLost} />;
  }
  // The current Studio's running tile, not a flat icon (spec ui-switch, e9).
  if (!isSettled(job.status)) return <RunningMedia job={job} />;
  const Icon = medium === "audio" ? AudioLines : medium === "video" ? Film : ImageIcon;
  return <Icon size={28} strokeWidth={1.5} className={classes.mediaIcon} aria-hidden />;
}

function Download({ job, onLost }: { job: Job; onLost: () => void }) {
  const output = shownOutput(job);
  // The hook needs an output; a ready job with no file offers no download.
  if (output === undefined) return null;
  return <DownloadButton job={job} output={output} onLost={onLost} />;
}

function DownloadButton(props: { job: Job; output: Job["outputs"][number]; onLost: () => void }) {
  const download = useDownloadOutput(props.job, props.output, props.onLost);
  return (
    <>
      <button
        type="button"
        className={classes.smallButton}
        disabled={download.busy}
        onClick={download.start}
      >
        Download
      </button>
      {download.result}
    </>
  );
}

function ContinueCard(props: { job: Job; model: string | null; onLost: () => void; onOpen: () => void }) {
  const { job, model, onLost, onOpen } = props;
  const medium = mediumOf(job) ?? "image";
  // Ready, but its files gone, never saved: the line in a plain badge, and no next step on it.
  const gone = job.status === "succeeded" && shownOutput(job) === undefined && job.outputs.some(isExpired);
  const status = gone ? { text: expiredLine(job), tone: undefined } : STATUS[job.status];
  const next = gone ? null : nextStep(job, medium);
  const name = TYPE_NAMES[job.type].name;
  return (
    <article className={classes.continueCard} aria-label={name}>
      {/* The picture opens the job's detail over Home, as a result on Generations does (Pro e2e
          batch 1); the next step and Download keep their own buttons below it. */}
      <button
        type="button"
        className={`${classes.media} ${classes.openMedia}`}
        aria-label={`Open this ${name.toLowerCase()}`}
        onClick={onOpen}
      >
        <Picture job={job} medium={medium} onLost={onLost} />
      </button>
      <span className={classes.facts}>
        <span className={classes.badge}>{name}</span>
        {model === null ? null : <span className={classes.model}>{model}</span>}
      </span>
      <span className={classes.badge} data-tone={status.tone}>
        {status.text}
      </span>
      <span className={classes.actions}>
        {!isSettled(job.status) ? (
          <span className={classes.tiny}>We let you know when it is done</span>
        ) : next === null ? null : (
          <SoonControl className={classes.smallButton}>{next}</SoonControl>
        )}
        {job.status === "succeeded" ? <Download job={job} onLost={onLost} /> : null}
      </span>
    </article>
  );
}

/** The prototype's "Continue": the user's unfinished work first, then their latest results. */
export function ContinueSection({ teamId, brandId }: { teamId: string; brandId: string }) {
  const userId = useQuery({ ...accessScopeQuery(), select: (scope) => scope.user_id }).data;
  const jobs = useJobList(teamId, { status: "all", brandId, pageSize: READ }, useStreamStatus());
  const catalog = useGetCatalog().data;
  const first = jobs.data?.pages[0]?.items;
  const shown = first === undefined ? undefined : continueJobs(first, userId);
  const readAgain = () => void jobs.refetch();
  // The job whose detail is open over Home, or none.
  const [open, setOpen] = useState<string | null>(null);
  return (
    <section aria-labelledby="home2-continue" className={classes.section}>
      <SectionHead title="Continue" id="home2-continue">
        {/* The prototype's "All generations →" opens the Generations page. */}
        <Link
          to="/teams/$teamId/brands/$brandId/home2/generations"
          params={{ teamId, brandId }}
          className={classes.subtle}
        >
          All generations <ArrowRight size={14} aria-hidden />
        </Link>
      </SectionHead>
      <p className={classes.lead}>
        Your unfinished work first, then your latest results with the usual next step.
      </p>
      {jobs.isError ? <ErrorAlert error={jobs.error} /> : null}
      {shown?.length === 0 ? (
        <p className={classes.small}>Nothing yet. What you make in this brand shows here.</p>
      ) : (
        <div className={classes.continue}>
          {shown === undefined
            ? [0, 1, 2, 3, 4].map((each) => (
                <Skeleton key={each} className={classes.mediaSkeleton} />
              ))
            : shown.map((job) => (
                <ContinueCard
                  key={job.id}
                  job={job}
                  model={modelTitle(catalog, job)}
                  onLost={readAgain}
                  onOpen={() => setOpen(job.id)}
                />
              ))}
        </div>
      )}
      {open === null ? null : (
        <GenerationDetailModal teamId={teamId} jobId={open} onClose={() => setOpen(null)} />
      )}
    </section>
  );
}
